// Adjustable-speed playback clock.
//
// Uses requestAnimationFrame + an accumulator (NOT setInterval) so that:
//   - playback auto-pauses when the tab/PWA is backgrounded (battery friendly),
//   - ticks stay frame-aligned with the chart's canvas redraw,
//   - high speeds catch up smoothly (multiple ticks per frame).
//
// All host APIs are injectable so the cadence is deterministically unit-testable.

export interface Clock {
  start(barsPerSec: number): void
  setSpeed(barsPerSec: number): void
  stop(): void
  readonly running: boolean
}

export interface ClockDeps {
  now: () => number // milliseconds
  requestFrame: (cb: (t: number) => void) => number
  cancelFrame: (id: number) => void
}

function defaultDeps(): ClockDeps {
  const g = globalThis as typeof globalThis & {
    requestAnimationFrame?: (cb: (t: number) => void) => number
    cancelAnimationFrame?: (id: number) => void
  }
  const hasRaf = typeof g.requestAnimationFrame === 'function'
  return {
    now: () => (typeof performance !== 'undefined' ? performance.now() : Date.now()),
    requestFrame: hasRaf
      ? (cb) => g.requestAnimationFrame!(cb)
      : (cb) => setTimeout(() => cb(Date.now()), 16) as unknown as number,
    cancelFrame: hasRaf
      ? (id) => g.cancelAnimationFrame!(id)
      : (id) => clearTimeout(id),
  }
}

/**
 * @param onTick called once per "bar" interval. If a frame is slow, it may be
 *   called multiple times to catch up (bounded by MAX_CATCHUP per frame).
 */
export function createClock(onTick: () => void, deps: Partial<ClockDeps> = {}): Clock {
  const d: ClockDeps = { ...defaultDeps(), ...deps }
  const MAX_CATCHUP = 64 // never reveal more than this many bars in one frame

  let rafId = 0
  let last = 0
  let primed = false
  let acc = 0
  let intervalMs = 1000
  let running = false

  const frame = (t: number) => {
    if (!running) return
    if (!primed) {
      last = t
      primed = true
    }
    acc += t - last
    last = t
    let n = 0
    while (acc >= intervalMs && n < MAX_CATCHUP) {
      onTick()
      acc -= intervalMs
      n++
    }
    // Drop excess backlog so we don't spiral after a long stall.
    if (acc > intervalMs * MAX_CATCHUP) acc = 0
    if (running) rafId = d.requestFrame(frame)
  }

  return {
    start(barsPerSec: number) {
      intervalMs = 1000 / Math.max(0.0001, barsPerSec)
      last = 0
      primed = false
      acc = 0
      running = true
      rafId = d.requestFrame(frame)
    },
    setSpeed(barsPerSec: number) {
      intervalMs = 1000 / Math.max(0.0001, barsPerSec)
    },
    stop() {
      running = false
      if (rafId) d.cancelFrame(rafId)
      rafId = 0
      last = 0
      primed = false
      acc = 0
    },
    get running() {
      return running
    },
  }
}

export type ClockFactory = (onTick: () => void) => Clock
